import { defineStore } from 'pinia'
import { useApi, useLocalStorage } from '@/shared/composables'
import {
  type GetTravelPricePayload,
  type GetTravelQuoteUaeResponse,
  type Plan,
  PromoCodeTypes,
} from '@/shared/types/api.types'
import type { State } from './quoteStore.types'
import {
  ACTIVE_ADDONS_KEY,
  ADDONS_LIST_KEY,
  ADDONS_PRICE_KEY,
  INSURED_TRAVELERS_KEY,
  IS_SENT_PURCHASE_ANALYTICS_EVENT,
  QUOTE_NO_KEY,
  PURCHASED_QUOTE_NO_KEY,
  TOTAL_PRICE_KEY,
  PRODUCT_BONUS_KEY,
  COINS_DISCOUNT_PRICE_KEY,
  QUOTE_WITH_COINS_DISCOUNT_KEY,
} from '@/shared/store/quoteStore/quoteStore.constants'

export const useQuoteStore = defineStore('quoteStore', {
  state: (): State => {
    const storage = useLocalStorage()
    return {
      quoteNo: storage.get(QUOTE_NO_KEY) ?? 0,
      purchasedQuoteNo: storage.get(PURCHASED_QUOTE_NO_KEY) ?? 0,
      totalPrice: storage.get(TOTAL_PRICE_KEY) ?? 0,
      addonsList: storage.get(ADDONS_LIST_KEY) ?? [],
      addonsPrice: storage.get(ADDONS_PRICE_KEY) ?? 0,
      activeAddons: storage.get(ACTIVE_ADDONS_KEY) ?? 0,
      insuredTravelers: storage.get(INSURED_TRAVELERS_KEY) ?? 0,
      isSentPurchaseAnalyticsEvent: storage.get(IS_SENT_PURCHASE_ANALYTICS_EVENT) ?? false,
      productBonus: storage.get(PRODUCT_BONUS_KEY) ?? 0,
      quoteWithCoinsDiscount: storage.get(QUOTE_WITH_COINS_DISCOUNT_KEY) ?? null,
      coinsDiscountPrice: storage.get(COINS_DISCOUNT_PRICE_KEY) ?? null,
    }
  },
  actions: {
    getTravelQuoteUae(
      promocode?: string,
      fullquote: 0 | 1 = 0,
      countries?: string[],
      type?: PromoCodeTypes
    ): Promise<GetTravelQuoteUaeResponse> {
      const api = useApi()
      return api.quoteService.getTravelQuoteUae(promocode, fullquote, countries, type)
    },

    getTravelQuoteByScheme(
      scheme: string,
      promocode?: string,
      fullquote: 0 | 1 = 0,
      countries?: string[]
    ): Promise<Plan | undefined> {
      return this.getTravelQuoteUae(promocode, fullquote, countries).then((res) =>
        res.schemes.find((s) => s.schemecode === scheme)
      )
    },

    async getTravelPriceUae(
      quoteno: string,
      schemecode: string,
      productcode: string,
      ids?: string[],
      selectproduct: 0 | 1 = 0
    ): Promise<{
      taxAmount: number
      totalprice: number
    }> {
      const api = useApi()
      return await api.quoteService.getTravelPriceUae(
        quoteno,
        schemecode,
        productcode,
        ids,
        selectproduct
      )
    },

    getTravelTotalPrice(payload: GetTravelPricePayload) {
      const api = useApi()
      return api.quoteService.getTravelTotalPrice(payload)
    },

    setQuoteNo(quoteNo: number, withLocalStorage = false): void {
      this.quoteNo = quoteNo
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(QUOTE_NO_KEY, quoteNo)
      }
    },

    setPurchasedQuoteNo(quoteNo: number, withLocalStorage = false): void {
      this.purchasedQuoteNo = quoteNo
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(PURCHASED_QUOTE_NO_KEY, quoteNo)
      }
    },

    setTotalPrice(totalPrice: number, withLocalStorage = false): void {
      this.totalPrice = totalPrice
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(TOTAL_PRICE_KEY, totalPrice)
      }
    },

    setAddonsList(addonsList: string[], withLocalStorage = false): void {
      this.addonsList = addonsList
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(ADDONS_LIST_KEY, addonsList)
      }
    },

    setAddonsPrice(addonsPrice: number, withLocalStorage = false): void {
      this.addonsPrice = addonsPrice
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(ADDONS_PRICE_KEY, addonsPrice)
      }
    },

    setActiveAddons(activeAddons: number, withLocalStorage = false): void {
      this.activeAddons = activeAddons
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(ACTIVE_ADDONS_KEY, activeAddons)
      }
    },

    setInsuredTravelers(insuredTravelers: number, withLocalStorage = false): void {
      this.insuredTravelers = insuredTravelers
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(INSURED_TRAVELERS_KEY, insuredTravelers)
      }
    },

    setIsSentPurchaseAnalyticsEvent(
      isSentPurchaseAnalyticsEvent: boolean,
      withLocalStorage = false
    ): void {
      this.isSentPurchaseAnalyticsEvent = isSentPurchaseAnalyticsEvent
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(IS_SENT_PURCHASE_ANALYTICS_EVENT, isSentPurchaseAnalyticsEvent)
      }
    },

    setProductBonus(productBonus: number, withLocalStorage = false): void {
      this.productBonus = productBonus
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(PRODUCT_BONUS_KEY, productBonus)
      }
    },

    setQuoteWithCoinsDiscount(
      quoteWithCoinsDiscount?: string | number,
      withLocalStorage = true
    ): void {
      if (!quoteWithCoinsDiscount) {
        this.quoteWithCoinsDiscount = null
      }
      this.quoteWithCoinsDiscount = Number(quoteWithCoinsDiscount)
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(QUOTE_WITH_COINS_DISCOUNT_KEY, quoteWithCoinsDiscount)
      }
    },

    setCoinsDiscountPrice(coinsDiscountPrice?: number | string, withLocalStorage = true): void {
      if (!coinsDiscountPrice) {
        this.coinsDiscountPrice = null
      }
      this.coinsDiscountPrice = Number(coinsDiscountPrice)
      if (withLocalStorage) {
        const storage = useLocalStorage()
        storage.setOrUpdate(COINS_DISCOUNT_PRICE_KEY, coinsDiscountPrice)
      }
    },
  },
})
